import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useRef } from "react";
import { COLORS, API } from "../constants";

function ToolbarButton({ onClick, active, title, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        padding: "5px 8px", fontSize: "13px", background: active ? COLORS.blueLight : "transparent",
        border: "none", borderRadius: "5px", cursor: "pointer", color: active ? COLORS.blue : COLORS.textSecondary,
        display: "flex", alignItems: "center", justifyContent: "center", minWidth: "28px"
      }}
    >
      {children}
    </button>
  );
}

export default function NoteEditor({ content, onChange, noteId, token }) {
  const imageInputRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { style: `color: ${COLORS.blue}; text-decoration: underline;` } }),
      Image,
      Placeholder.configure({ placeholder: "Start typing your notes..." }),
    ],
    content: content || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        style: "outline: none; padding: 16px 18px; font-size: 14px; line-height: 1.6; min-height: 100%;"
      }
    }
  });

  // Sync editor content when switching tabs (content prop changes to a different note)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, content]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("Enter URL:");
    if (!url) return;
    if (editor.state.selection.empty) {
      editor.chain().focus().insertContent(`<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`).run();
    } else {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    imageInputRef.current?.click();
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(`${API}/notes/${noteId}/attachments`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData
    });
    const attachment = await res.json();
    const imageUrl = `${API}/notes/attachments/${attachment.id}/preview?token=${token}`;
    editor.chain().focus().setImage({ src: imageUrl, alt: attachment.filename }).run();
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: "2px", padding: "6px 12px", borderBottom: `0.5px solid ${COLORS.border}`, flexWrap: "wrap" }}>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold"><b>B</b></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic"><i>I</i></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline"><u>U</u></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough"><s>S</s></ToolbarButton>
        <div style={{ width: "1px", height: "18px", background: COLORS.border, margin: "0 4px" }} />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list">☰</ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list">☱</ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote">"</ToolbarButton>
        <div style={{ width: "1px", height: "18px", background: COLORS.border, margin: "0 4px" }} />
        <ToolbarButton onClick={addLink} active={editor.isActive("link")} title="Add link">🔗</ToolbarButton>
        <ToolbarButton onClick={addImage} title="Add image">🖼️</ToolbarButton>
        <div style={{ width: "1px", height: "18px", background: COLORS.border, margin: "0 4px" }} />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo">↶</ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo">↷</ToolbarButton>
      </div>

      {/* Editor content */}
      <div style={{ flex: 1, overflow: "auto" }}>
        <EditorContent editor={editor} />
      </div>

      <input type="file" accept="image/*" ref={imageInputRef} onChange={handleImageFileChange} style={{ display: "none" }} />
    </div>
  );
}